import { History } from 'history';
import _ from 'lodash';

import { getCurrentLocation } from './HistoryProvider';
import * as DevOps from '../devOpsHelper/DevOps';

const WHITE_LISTED_PARAMS = ['overlay', 'state_key'];

export function getQueryParameter(
    location: Location | History['location'],
    queryParameter: string
) {
    const regExp = new RegExp('[?&]' + queryParameter + '=([^&#]*)', 'i');
    if (!_.get(location, 'search')) return;
    const match = regExp.exec(location.search!);

    if (match) {
        return match[1];
    }
}

export function redirectToLogin() {
    if (!DevOps.isStorybookEnv()) {
        if (window.location.pathname === '/account/link-open-banking/') {
            window.location.assign(
                `/login/?redirect_to=${encodeURIComponent(
                    '/account/link-open-banking/'
                )}`
            );
        } else if (
            window.location.pathname === '/account/credit-card/consent-review/'
        ) {
            window.location.assign(
                `/login/?persist_params=1&redirect_to=${encodeURIComponent(
                    window.location.pathname + window.location.search
                )}`
            );
        } else {
            window.location.assign(`/login/`);
        }
    }
}

export function isLinkingOpenBankingOnly() {
    return window.location.pathname === '/account/link-open-banking/';
}

export function addQueryParameterInLocation(
    currentLocation: Location,
    queryParameterName: string,
    value: string
) {
    const search = updateQueryStringParameter(
        currentLocation && currentLocation.search,
        queryParameterName,
        value
    );

    return {
        ...currentLocation,
        search
    };
}

export function removeQueryParameterInLocation(
    currentLocation: Location,
    queryParameterName: string
) {
    const search = removeQueryStringParameter(
        currentLocation && currentLocation.search,
        queryParameterName
    );
    return {
        ...currentLocation,
        search
    };
}

function decomposeQueryStringParameters(search: typeof window.location.search) {
    const parameters = [];
    const keyValueParameters =
        search && search[0] === '?' ? search.substr(1).split('&') : [];

    for (let index = 0; index < keyValueParameters.length; index++) {
        const pair = keyValueParameters[index].split('=');
        parameters.push({
            key: decodeURIComponent(pair[0]),
            value: decodeURIComponent(pair[1])
        });
    }

    return parameters;
}

function removeQueryStringParameter(
    search: typeof window.location.search,
    key: string
) {
    const parameters = decomposeQueryStringParameters(search);
    const reducedParameters = _.filter(parameters, (pair) => pair.key !== key);
    return serialiseQueryStrinng(reducedParameters);
}

function updateQueryStringParameter(
    search: typeof window.location.search,
    key: string,
    value: string
) {
    const parameters = decomposeQueryStringParameters(search);
    const index = _.findIndex(parameters, ['key', key]);

    if (index === -1) {
        parameters.push({ key, value });
    } else {
        parameters.splice(index, 1, { key, value });
    }

    return serialiseQueryStrinng(parameters);
}

function serialiseQueryStrinng(parameters: { key: string; value: string }[]) {
    const serialisedPairs = _.map(
        parameters,
        (pair) =>
            encodeURIComponent(pair.key) + '=' + encodeURIComponent(pair.value)
    );
    const temporarySearch = '?' + serialisedPairs.join('&');
    return _.trimEnd(temporarySearch, '?');
}

export function buildEvent(
    { location, action }: { location: Location; action: any },
    disableFirstRouterChange: boolean
) {
    const impersonatingStateKey = getQueryParameter(location, 'state_key');
    const mainEvent = {
        location,
        action,
        disableFirstRouterChange
    };

    if (impersonatingStateKey) {
        return {
            ...mainEvent,
            impersonatingStateKey
        };
    }
    return mainEvent;
}

export function reloadCurrentLocation() {
    return window.location.reload();
}

export function getCurrentHostname() {
    return window.location.hostname;
}

export function getCurrentPathname() {
    return window.location.pathname;
}

export function getWindowLocation() {
    return window.location;
}

export function decomposeURLIntoLocation(partialURL: string) {
    //TODO parser instance should be a singleton in case of an a tag
    const parser = document.createElement('a');
    parser.href = partialURL;

    return parser;
}

export const maintainStateKey = (to: {
    pathname?: string;
    search?: string;
    hash?: string;
}) => {
    const currentStateKey = getQueryParameter(
        getCurrentLocation(),
        'state_key'
    );

    if (!currentStateKey) return to;

    const currentParams = to.search?.substring(1).split('&');

    if (currentParams) {
        const currentParamsWithoutStateKey = currentParams?.filter(
            (param: string) => param.split('=')[0] !== 'state_key'
        );

        const newParams = [
            ...currentParamsWithoutStateKey,
            `state_key=${currentStateKey}`
        ].join('&');

        return {
            ...to,
            search: newParams
        };
    }

    return {
        ...to,
        search: `?state_key=${currentStateKey}`
    };
};

export const buildWhiteListedSearchParams = () => {
    const params = new URLSearchParams(window.location.search);
    const hydratedSearchParams = new URLSearchParams();

    for (const WHITE_LISTED_PARAM of WHITE_LISTED_PARAMS) {
        const param = params.get(WHITE_LISTED_PARAM);
        if (param) hydratedSearchParams.append(WHITE_LISTED_PARAM, param);
    }
    return hydratedSearchParams;
};
